import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Breadcrumb from '../../components/Common/Breadcrumb';
import DataTable from '../../components/DataTable/DataTable';
import { burnExpenseService } from '../../services/burnExpenseService';
import { toast } from 'react-toastify';

const BurnReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState({ monthlyTrend: [], categoryBreakdown: [] });
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 5);
    return d.toISOString().split('T')[0].slice(0, 7);
  });
  const [to, setTo] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    loadData();
  }, [from, to]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [fromY, fromM] = from.split('-').map(Number);
      const [toY, toM] = to.split('-').map(Number);
      const fromDate = `${fromY}-${String(fromM).padStart(2, '0')}-01`;
      const toDate = `${toY}-${String(toM).padStart(2, '0')}-28`;
      const res = await burnExpenseService.getBurnTrend({ from: fromDate, to: toDate });
      if (res?.success && res.data) {
        setTrend({
          monthlyTrend: res.data.monthlyTrend || [],
          categoryBreakdown: res.data.categoryBreakdown || []
        });
      }
    } catch (err) {
      toast.error('Failed to load burn trend');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(v || 0);

  const breadcrumbItems = [
    { label: 'Expenses', path: '/expenses', onClick: () => navigate('/expenses') },
    { label: 'Reports', path: '/expenses?tab=reports', onClick: () => navigate('/expenses?tab=reports') },
    { label: 'Burn Reports', path: '/expenses/burn-reports', isCurrent: true }
  ];

  const categoryColumns = [
    { accessorKey: 'category', header: 'Category' },
    { accessorKey: 'group', header: 'Group', cell: ({ getValue }) => <span className="badge bg-primary">{getValue()}</span> },
    { accessorKey: 'total', header: 'Total', cell: ({ getValue }) => formatCurrency(getValue()) }
  ];

  const categoryData = (trend.categoryBreakdown || []).map((r, i) => ({ ...r, id: i }));

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section"><Breadcrumb items={breadcrumbItems} /></div>
      <div className="page-header">
        <div className="page-title">
          <h1>Burn Reports</h1>
          <p>Monthly burn trend and category breakdown</p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <label className="form-label mb-0">From:</label>
          <input type="month" className="form-control" style={{ width: 'auto' }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <label className="form-label mb-0">To:</label>
          <input type="month" className="form-control" style={{ width: 'auto' }} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      <div className="dashboard-chart-card revenue-chart" style={{ marginBottom: '24px' }}>
        <div className="chart-header"><h3>Burn Trend (Monthly)</h3><p>Comparison by month</p></div>
        <div className="chart-content" style={{ height: 280 }}>
          {loading ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: '100%' }}>Loading...</div>
          ) : trend.monthlyTrend?.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend.monthlyTrend.map(r => ({ ...r, month: r.month }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(v) => `₹${v / 1000}k`} />
                <Tooltip formatter={(v) => [formatCurrency(v, 'INR'), 'Total']} />
                <Bar dataKey="total" fill="#0d6efd" name="Burn" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="d-flex align-items-center justify-content-center text-muted" style={{ height: '100%' }}>No data</div>
          )}
        </div>
      </div>

      <div className="dashboard-chart-card">
        <div className="chart-header"><h3>Category Breakdown</h3><p>Expenses by category</p></div>
        <div className="chart-content">
          <DataTable data={categoryData} columns={categoryColumns} loading={loading} noCard pageSize={15} />
        </div>
      </div>
    </div>
  );
};

export default BurnReports;
